import { Component, inject, OnInit } from '@angular/core';
import { UntypedFormBuilder, UntypedFormControl } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { EdpdStorageService } from '@eredes/dfcore';
import { DSM3Pagination } from '@eredes/dsm3';
import { DSM34Dialog, DSM34DialogRef } from '@eredes/dsm34';
import * as FileSaver from 'file-saver';
import moment from 'moment';

import { ApiMsService, ApiMsServices } from '../../_core/services/api-ms.service';
import { GlobalService } from '../../_core/services/global.service';
import { UserService } from '../../_core/services/user.service';
import { EdpTableComponent } from '../../_shared/edp-table/edp-table.component';
import { EdpTableInterface } from '../../_shared/edp-table/edp-table.interface';
import { GenericModalComponent } from '../../_shared/generic-modal/generic-modal.component';
import { AuthService } from '../../auth/auth.service';
import { ModalComponent, UserDataResponse, UserViewModel } from '../user.view-model';

import { UserListViewModel } from './user-list.view-model';

@Component({
  selector: 'app-user-list',
  templateUrl: './user-list.component.html',
  standalone: false,
})
export class UserListComponent extends EdpTableComponent implements OnInit, EdpTableInterface {
  viewModel = new UserListViewModel();

  public dialog = inject(DSM34Dialog);

  constructor(
    protected userService: UserService,
    public authService: AuthService,
    protected globalService: GlobalService,
    protected router: Router,
    protected route: ActivatedRoute,
    private readonly apiMsService: ApiMsService,
    private readonly fb: UntypedFormBuilder
  ) {
    super(userService, authService, globalService);
  }

  ngOnInit() {
    super.ngOnInit();
    this.order = 'user_name';
    this.direction = 'ASC';
    this.createForm();
    this.refreshData(0);
  }

  /**
   * When user clicks on search button, receives emited value from
   * dsm3 search-control and does a search with that value
   * @param searchTerm - value emitted from search-control component
   */
  onSearch(searchTerm: string): void {
    this.searchTerm = searchTerm;
    this.viewModel.currentUserRequestPage = 1;
    this.refreshData(0);
  }

  /**
   * Creates form for filters values
   */
  createForm(): void {
    this.viewModel.formGroup = this.fb.group({
      search: new UntypedFormControl('', {
        validators: [],
        updateOn: 'change',
      }),
    });

    /**
     * if when valuuChange is empty (when user clicks on "clear" in search-control component)
     * does a new search with that empty value : default search
     */
    this.viewModel.formGroup.get('search').valueChanges.subscribe((v) => {
      if (v === '') {
        this.searchTerm = v;
        this.viewModel.currentUserRequestPage = 1;
        this.refreshData(0);
      }
    });
  }

  /**
   * Update datas on the page
   * @param skip
   */
  refreshData(skip: number) {
    const args: any = {
      filters: {},
      page: (this.top + skip) / this.top,
      perPage: this.viewModel.paginationPerPage,
      orderBy: this.order,
      orderDir: this.direction,
    };

    if (this.userService.isProfileRetailer()) {
      args.filters.retailerUniqueCode = this.userService.getRetailerUniqueCode();
    }

    if (this.searchTerm !== '') {
      args.filters.search = this.searchTerm;
    }

    this.apiMsService.call<UserDataResponse>(ApiMsServices.Comercializadores, `user/get-list`, args).subscribe(
      (result: UserDataResponse) => {
        this.tableData = result.data;
        this.skip = result.perPage * result.currentPage - result.perPage;
        this.top = result.perPage;
        this.totalItems = result.totalItems;
        this.viewModel.isErrorFromService = false;
        this.viewModel.isEmptyList = this.totalItems <= 0;
      },
      () => {
        this.tableData = [];
        this.viewModel.isErrorFromService = true;
      }
    );
  }

  /**
   *
   * @returns MatDialogRef Generic modal component where open a modal
   */
  protected userDeletedSuccessModal(): DSM34DialogRef<GenericModalComponent, ModalComponent> {
    return this.dialog.open(GenericModalComponent, {
      data: {
        modalTitle: 'Sucesso',
        modalText: 'Utilizador apagado com sucesso.',
        success: true,
        btnConfirmText: 'Ok',
      },
    });
  }

  /**
   *
   * @returns MatDialogRef Generic modal component where open a modal
   */
  protected userDeletedErrorModal(): DSM34DialogRef<GenericModalComponent, ModalComponent> {
    return this.dialog.open(GenericModalComponent, {
      data: {
        modalTitle: 'Erro',
        modalText: 'Ocorreu um problema, por favor volte a tentar.',
        error: true,
        btnConfirmText: 'Ok',
      },
    });
  }

  /**
   * Delete a user
   * @param userUniqueCode
   * @param userUserAccount
   * @param username
   * @param retailerUniqueCode
   * @param user
   */
  deleteUser(user: UserViewModel): void {
    const dialogRef = this.dialog.open(GenericModalComponent, {
      data: {
        modalTitle: 'Confirmação',
        modalText: 'Deseja eliminar o utilizador ' + user.userName + '?',
        warning: true,
        btnConfirmText: 'Confirmar',
        btnCancelText: 'Cancelar',
        height: '250px',
      },
      disableClose: false,
    });
    dialogRef.afterClosed().subscribe(
      (result) =>
        result &&
        this.apiMsService.call(ApiMsServices.Comercializadores, 'user/delete', { user }).subscribe(
          () =>
            this.userDeletedSuccessModal()
              .afterClosed()
              .subscribe(() => this.refreshData(0)),
          () => this.userDeletedErrorModal().afterClosed().subscribe()
        )
    );
  }

  /**
   * Updates data as pagination changed
   * @param event - Object with next page and items per page
   */
  paginationChanged(event: DSM3Pagination): void {
    let skip: number;
    if (this.viewModel.paginationPerPage !== event.itemsPerPage) {
      this.viewModel.currentUserRequestPage = 1;
      this.viewModel.paginationPerPage = event.itemsPerPage;
      skip = 0;
    } else if (event.page !== this.viewModel.currentUserRequestPage) {
      if (event.page === 1) {
        skip = 0;
      } else {
        skip = this.viewModel.paginationPerPage * (event.page - 1);
        this.viewModel.currentUserRequestPage = event.page;
      }
    }

    this.refreshData(skip);
    this.skip = skip;
  }

  /**
   * It calls an API that returns a CSV file, which is then saved to the user's computer.
   */
  onExport() {
    const args: any = {
      filters: {},
      page: 1,
      perPage: this.totalItems,
      orderBy: this.order,
      orderDir: this.direction,
    };

    if (this.userService.isProfileRetailer()) {
      args.filters.retailerUniqueCode = this.userService.getRetailerUniqueCode();
    }

    if (this.searchTerm !== '') {
      args.filters.search = this.searchTerm;
    }

    this.apiMsService.call<string>(ApiMsServices.Comercializadores, `user/export`, args).subscribe(
      (result) => {
        const date = moment(new Date()).format('YYYYMMDDHHmmss');
        const file = EdpdStorageService.base64toBlob(result, '');
        FileSaver.saveAs(file, `utilizadores_${date}.csv`);
      },
      (error) => {
        return this.dialog.open(GenericModalComponent, {
          data: {
            modalTitle: 'Erro',
            modalText: 'Ocorreu um problema, por favor volte a tentar.',
            error: true,
            btnConfirmText: 'Ok',
          },
        });
      }
    );
  }
}
